import React, { useRef } from 'react';
import styled from 'styled-components';
import { SxProps, Typography, Tooltip, Theme } from '@mui/material';
import { Flex, FlexColumn } from '../Flex';
import useIsOverflow from '../../hooks/useIsOverflow';
import {
  SourceFreshnessConfiguration,
  SourceFreshnessTestResult,
  SourceFreshnessTestRun,
  TestConfiguration,
  TestResult,
  TestRun,
  AnomalyDirection,
} from '../../types';
import {
  capitalize,
  pluralizeString,
  isElementaryCloudTest,
  isContentTest,
} from '../../utils/common';
import { getTimeDiffParts, localizeDate } from '../../utils/datetime';
import OverflowClipboard from '../OverflowClipboard';
import OverflowInfo from '../OverflowInfo';
import { fontSize, fontWeight, radius, space } from '../../style/utils';

const LOADED_AT_COLUMN_TITLE = '"Loaded at" column:';

const Wrapper = styled(Flex)`
  gap: ${space('md')};
  justify-content: space-between;
`;

const Section = styled(FlexColumn)`
  gap: ${space('sm')};
  width: 33%;
`;

const Card = styled(FlexColumn)`
  background: ${({ theme }) => theme.palette.surface.sunken};
  padding: ${space('md')};
  border-radius: ${radius('lg')};
  justify-content: space-between;
  gap: ${space('lg')};
  height: 180px;
`;

const Title = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const DetailTitle = styled(Typography)`
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('medium')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const DetailValueWrapper = styled(Typography)<{ $maxRows?: number }>`
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $maxRows }) => $maxRows || 3};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const Detail = styled(FlexColumn)`
  gap: ${space('xs')};
`;

const DetailRow = styled(Flex)<{ $childCount?: number }>`
  flex-wrap: wrap;
  height: 100%;
  align-content: space-between;
  > div {
    width: ${({ $childCount }) =>
      $childCount == null || $childCount > 2 ? '50%' : '100%'};
  }
`;

const getSourceFreshnessTimeFrameConfiguration = (
  count?: number | null,
  period?: 'minute' | 'hour' | 'day' | null,
) => {
  if (!count || !period) {
    return 'Not configured';
  }

  return `${count} ${capitalize(period)}${count > 1 ? 's' : ''}`;
};

const getSourceFreshnessResultDescription = (data: SourceFreshnessTestRun) => {
  const {
    max_loaded_at: maxLoadedAt,
    max_loaded_at_time_ago_in_s: maxLoadedAtTimeAgoInSec,
    status,
    error_message: errorMessage,
  } = data.test_results;

  if (status === 'error') return errorMessage;
  if (maxLoadedAt && maxLoadedAtTimeAgoInSec) {
    const { days, hours, minutes, seconds } = getTimeDiffParts(
      maxLoadedAtTimeAgoInSec,
    );

    let formattedDelta = '';
    if (days > 0) {
      formattedDelta = `${pluralizeString(
        days,
        'day',
        'days',
      )} ${hours}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      formattedDelta = `${pluralizeString(
        hours,
        'hour',
        'hours',
      )} ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      formattedDelta = `${pluralizeString(
        minutes,
        'minute',
        'minutes',
      )} ${seconds}s`;
    } else {
      formattedDelta = `${pluralizeString(seconds, 'second', 'seconds')}`;
    }

    const detectedAt = localizeDate(maxLoadedAt).add(
      maxLoadedAtTimeAgoInSec,
      'seconds',
    );
    return `When the test ran at ${localizeDate(detectedAt).format(
      'L LTS',
    )}, the most recent record found in the table was ${formattedDelta} earlier. The most recent record found was at ${localizeDate(
      maxLoadedAt,
    ).format('L LTS')}.`;
  }
  return 'No result description';
};

const DetailValue = ({
  children,
  maxRows,
  sx,
}: {
  children: React.ReactNode;
  maxRows?: number;
  sx?: SxProps<Theme>;
}) => {
  const ref = useRef<any>();
  const isOverflow = useIsOverflow(ref);

  return (
    <Tooltip title={children} disableHoverListener={!isOverflow}>
      <DetailValueWrapper ref={ref} sx={sx} $maxRows={maxRows}>
        {children}
      </DetailValueWrapper>
    </Tooltip>
  );
};

const AnomalyDirectionValue = ({
  isSelected,
  value,
}: {
  isSelected: boolean;
  value: string;
}) => (
  <DetailValue
    sx={{
      fontWeight: isSelected ? 'bold' : null,
      color: isSelected ? 'content.primary' : 'content.placeholder',
    }}
  >
    {value}
  </DetailValue>
);

const EmptyTestDescription = ({
  onOpenSnackbar,
}: {
  onOpenSnackbar?: (message: string) => void;
}) => (
  <>
    <DetailValue
      sx={{ color: 'content.secondary', height: '50px' }}
      maxRows={2}
    >
      No description is configured for this test, use the following snippet to
      add it.
    </DetailValue>
    <OverflowClipboard
      sx={{
        padding: (theme) => `${theme.tokens.space.sm} !important`,
      }}
      value={`meta:\n    description:\n        "Add your description here"`}
      fullWidth
      onOpenSnackbar={onOpenSnackbar}
    />
  </>
);

const TestConfigurationSection = ({
  configuration,
  isCloudTest,
  severity,
}: {
  configuration: TestConfiguration;
  isCloudTest: boolean;
  severity?: string;
}) => (
  <DetailRow $childCount={Object.keys(configuration).length}>
    {!isCloudTest && (
      <Detail>
        <DetailTitle>Test Name:</DetailTitle>
        <DetailValue>{configuration?.test_name || 'No test name'}</DetailValue>
      </Detail>
    )}
    {isCloudTest && (
      <Detail>
        <DetailTitle>Sensitivity:</DetailTitle>
        <DetailValue>
          {capitalize(configuration?.sensitivity) || ''}
        </DetailValue>
      </Detail>
    )}
    {configuration?.testing_timeframe !== undefined && (
      <Detail>
        <DetailTitle>Timeframe:</DetailTitle>
        <DetailValue>{configuration?.testing_timeframe || 'Daily'}</DetailValue>
      </Detail>
    )}
    {configuration?.timestamp_column !== undefined && (
      <Detail>
        <DetailTitle>Timestamp Column:</DetailTitle>
        <DetailValue>
          {configuration?.timestamp_column || 'No timestamp column'}
        </DetailValue>
      </Detail>
    )}
    {configuration?.anomaly_threshold !== undefined && !isCloudTest && (
      <Detail>
        <DetailTitle>Anomaly Threshold:</DetailTitle>
        <DetailValue>
          {configuration?.anomaly_threshold || 'No anomaly threshold'}
        </DetailValue>
      </Detail>
    )}
    {configuration?.anomaly_direction !== undefined && (
      <Detail>
        <DetailTitle>Anomaly Direction:</DetailTitle>
        <DetailRow
          divider={
            <Typography sx={{ color: 'content.placeholder', mx: 0.5 }}>
              /
            </Typography>
          }
        >
          {Object.keys(AnomalyDirection).map((direction) => (
            <AnomalyDirectionValue
              key={direction}
              isSelected={
                configuration?.anomaly_direction ===
                AnomalyDirection[direction as keyof typeof AnomalyDirection]
              }
              value={direction}
            />
          ))}
        </DetailRow>
      </Detail>
    )}
    {isCloudTest && (
      <Detail>
        <DetailTitle>Severity:</DetailTitle>
        <DetailValue>
          {severity ? capitalize(severity.toLowerCase()) : 'Error'}
        </DetailValue>
      </Detail>
    )}
    {configuration?.test_params !== undefined && !isCloudTest && (
      <Detail>
        <DetailTitle>Test Params:</DetailTitle>
        {configuration?.test_params ? (
          <OverflowInfo
            value={JSON.stringify(configuration?.test_params)}
            fullWidth
          />
        ) : (
          <DetailValue>No test params</DetailValue>
        )}
      </Detail>
    )}
  </DetailRow>
);

const SourceFreshnessConfigurationSection = ({
  configuration,
}: {
  configuration: SourceFreshnessConfiguration;
}) => (
  <DetailRow>
    <Detail>
      <DetailTitle>Fail After:</DetailTitle>
      <DetailValue>
        {getSourceFreshnessTimeFrameConfiguration(
          configuration.error_after?.count,
          configuration.error_after?.period,
        )}
      </DetailValue>
    </Detail>
    <Detail>
      <DetailTitle>Warn After:</DetailTitle>
      <DetailValue>
        {getSourceFreshnessTimeFrameConfiguration(
          configuration.warn_after?.count,
          configuration.warn_after?.period,
        )}
      </DetailValue>
    </Detail>
    <Detail>
      <DetailTitle>{LOADED_AT_COLUMN_TITLE}</DetailTitle>
      <DetailValue>{configuration.loaded_at_field || 'N/A'}</DetailValue>
    </Detail>
  </DetailRow>
);

type Props = {
  data: TestRun | TestResult | SourceFreshnessTestResult;
  onOpenSnackbar?: (message: string) => void;
};

const ExpandedDetailsSection = ({ data, onOpenSnackbar }: Props) => {
  const isCloudTest =
    isElementaryCloudTest(data.metadata.test_type) ||
    isContentTest(data.metadata.test_sub_type);

  return (
    <Wrapper>
      <Section>
        <Title>Description</Title>
        <Card>
          <Detail>
            <DetailTitle>Test Description:</DetailTitle>
            {data.metadata.description ? (
              <DetailValue maxRows={6}>{data.metadata.description}</DetailValue>
            ) : (
              <EmptyTestDescription onOpenSnackbar={onOpenSnackbar} />
            )}
          </Detail>
        </Card>
      </Section>
      <Section>
        <Title>Result</Title>
        <Card>
          {data.metadata.test_type === 'source_freshness' ? (
            <Detail>
              <DetailTitle>Result Description:</DetailTitle>
              <DetailValue maxRows={6}>
                {getSourceFreshnessResultDescription(
                  data as SourceFreshnessTestRun,
                )}
              </DetailValue>
            </Detail>
          ) : (
            <>
              <Detail>
                <DetailTitle>Result Description:</DetailTitle>
                <DetailValue maxRows={isCloudTest ? 6 : 3}>
                  {data.metadata.result?.result_description ||
                    'No result description'}
                </DetailValue>
              </Detail>
              {!isCloudTest && (
                <Detail>
                  <DetailTitle>Result Query:</DetailTitle>
                  {data.metadata.test_query ? (
                    <OverflowClipboard
                      value={data.metadata.test_query}
                      autoTooltipPlacement
                      fullWidth
                      onOpenSnackbar={onOpenSnackbar}
                    />
                  ) : (
                    <DetailValue>No result query</DetailValue>
                  )}
                </Detail>
              )}
            </>
          )}
        </Card>
      </Section>
      <Section>
        <Title>Configuration</Title>
        <Card>
          {data.metadata.test_type === 'source_freshness' ? (
            <SourceFreshnessConfigurationSection
              configuration={
                data.metadata.configuration as SourceFreshnessConfiguration
              }
            />
          ) : (
            <TestConfigurationSection
              configuration={data.metadata.configuration as TestConfiguration}
              isCloudTest={isCloudTest}
              severity={data.metadata.severity}
            />
          )}
        </Card>
      </Section>
    </Wrapper>
  );
};

export default ExpandedDetailsSection;
